"use client";
import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { assessmentsBus } from "../utils/assessmentsBus";
import FullScreenLoader from "../components/FullScreenLoader";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { motion } from "framer-motion";
import { CheckIcon } from "@heroicons/react/24/solid";
import Alert from "../components/Alert";

import CryptoJS from "crypto-js";
const API_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/assessments";

const CLIENT_SHARED_SECRET = process.env?.REACT_APP_JWT_SECRET || "122Codeman!";

export default function AddAssessmentsNew() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const plan = state?.plan;

  const [alert, setAlert] = useState({
    open: false,
    variant: "info",
    title: "",
    message: "",
    primaryAction: null,
    secondaryAction: null,
  });
  // read token from localStorage once
  const authToken = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.authToken || null;
    } catch {
      return null;
    }
  }, []);
  // form state
  const [fullName, setFullName] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ui state
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  // form state
  const [companyName, setCompanyName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [stateProv, setStateProv] = useState("");
  const [country, setCountry] = useState("CA");
  const [vertical, setVertical] = useState("Vertical 1");
  const [keyContact, setKeyContact] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState("Owner");

  // ui state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const handleSubmit = async () => {
    setErr("");
    setOk("");

    if (!authToken) {
      setErr("Not authorized. Please register or log in.");
      console.log(err);
      return;
    }
    if (!companyName.trim()) {
      setErr("Company Name is required.");
      console.log(err);
      return;
    }
    const sanitizedName = companyName
      .replace(/[^a-zA-Z0-9]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const payload = {
      companyName: sanitizedName?.toUpperCase()?.trim(),
      address1: address1.trim(),
      address2: address2.trim(),
      city: city.trim(),
      state: stateProv,
      country,
      vertical,
      keyContact: keyContact.trim(),
      phone: phone.trim(),
      email: email.trim(),
      position,
    };

    try {
      setLoading(true);
      const res = await fetch(API_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken, // change to `Bearer ${authToken}` if needed
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        setAlert({
          open: true,
          variant: "success",
          title: "Saved Successfully",
          message:
            "Assessment has been saved successfully under 'Active Assessements'.",
        });

        assessmentsBus.emit();
        navigate(`/dashboard/${slugify(companyName)}/interviewSetup`);
      }

      setOk("Assessment saved successfully.");
    } catch (e) {
      setAlert({
        open: true,
        variant: "error",
        title: "Saving failed",
        message: "Failed to save assessment.",
      });
    } finally {
      setLoading(false);
    }
  };

  const [option, setoption] = useState("");

  return (
    <div className="w-full">
      {loading && <FullScreenLoader />}

      <Alert
        open={alert.open}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
        variant={alert.variant}
        title={alert.title}
        message={alert.message}
        primaryAction={alert.primaryAction}
        secondaryAction={alert.secondaryAction}
        autoCloseMs={alert.autoCloseMs}
      />

      <Dialog
        open={open}
        onClose={setOpen}
        className="relative z-10"
        style={{ zIndex: 99 }}
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div>
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary">
                  <CheckIcon aria-hidden="true" className="size-6 text-white" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold text-gray-900"
                  >
                    Registration successful
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      We’re excited to have you as part of our team! Browse
                      packages, get assessments, and track everything in one
                      place.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/dashboard/assessments/addAssessments");
                    }}
                    className={`w-full mt-4 px-4 py-2 text-sm font-semibold text-white bg-primary`}
                  >
                    Add Assessments
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full"
      >
        <main className="relative w-full overflow-hidden">
          <div className="relative z-10 mx-auto w-full">
            {/* Frosted panel */}
            <section
              className="
            mt-5 sm:mt-6 lg:mt-8
             border border-white/15 bg-white/5 bg-[#11406c]/40
            backdrop-blur-xs shadow-[0_10px_40px_rgba(0,0,0,0.35)]
            ring-1 ring-white/5
          "
            >
              <div>
                {/* Heading */}

                <h2 className="text-center font-semibold text-xl sm:text-2xl lg:text-3xl bg-black p-4">
                  Add An Assessments
                </h2>

                {/* Cards grid */}
                <div className="p-4 sm:p-6 lg:p-8 bg-[#000054] grid gap-4 sm:gap-6 lg:gap-8 grid-cols-1">
                  {/* Core */}
                  <div className="w-full md:w-3/4 mx-auto flex flex-col items-center text-center border-2 p-3 sm:p-4 bg-white/5 h-full max-h-[80vh] overflow-auto">
                    <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                      Enter all information and add to begin the assessment
                    </p>
                    <div className="flex flex-col md:flex-row gap-6 sm:gap-8 w-full mt-8">
                      <div className="flex flex-col items-center w-full md:w-1/2 px-2 sm:px-6">
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Company Name
                          </label>
                          <input
                            className="border border-primary1 text-primary1 px-3 py-1"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Address
                          </label>
                          <input
                            className="border border-primary1 text-primary1 px-3 py-1"
                            value={address1}
                            onChange={(e) => setAddress1(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Address 2
                          </label>
                          <input
                            className="border border-primary1 text-primary1 px-3 py-1"
                            value={address2}
                            onChange={(e) => setAddress2(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            City
                          </label>
                          <input
                            className="border border-primary1 text-primary1 px-3 py-1"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            State
                          </label>
                          <input
                            className="border border-primary1 text-primary1 px-3 py-1"
                            value={state}
                            onChange={(e) => setStateProv(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Country
                          </label>
                          <select
                            className="border border-primary text-sm text-primary1 px-1 py-1 h-8"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                          >
                            <option value="CA">Canada</option>
                            <option value="US">United States</option>
                          </select>
                        </div>

                        {err && (
                          <p className="text-red-600 text-md mt-2 mb-2 relative z-10">
                            {err}
                          </p>
                        )}
                        {ok && (
                          <p className="text-green-600 text-md mt-2 mb-2 relative z-10">
                            {ok}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-center w-full md:w-1/2 px-2 sm:px-6">
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Vertical
                          </label>
                          <select
                            className="border border-primary text-sm text-primary1 px-1 py-1 h-8"
                            value={vertical}
                            onChange={(e) => setVertical(e.target.value)}
                          >
                            <option value="Vertical 1">Vertical 1</option>
                            <option value="Vertical 2">Vertical 2</option>
                          </select>
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Key Contact
                          </label>
                          <input
                            className="border border-primary1 text-primary1 px-3 py-1"
                            value={keyContact}
                            onChange={(e) => setKeyContact(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Phone Number
                          </label>
                          <input
                            className="border border-primary1 text-primary1 px-3 py-1"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Email Address
                          </label>
                          <input
                            className="border border-primary1 text-primary1 px-3 py-1"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col space-y-1 w-full text-left mb-2">
                          <label className="text-sm font-medium text-white">
                            Position
                          </label>
                          <select
                            className="border border-primary text-sm text-primary1 px-1 py-1 h-8"
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                          >
                            <option value="Owner">Owner/President</option>
                            <option value="CEO">CEO</option>
                            <option value="CTO">CTO</option>
                          </select>
                        </div>
                        <div className="w-full">
                          <button
                            type="button"
                            className=" h-9
                        w-full justify-center bg-white px-5 mt-6
                        text-sm sm:text-base font-semibold text-black
                        shadow-sm border-2 border-primary1
                        hover:bg-[#285172] hover:text-white
                        
                      "
                            disabled={loading}
                            onClick={handleSubmit}
                          >
                            {loading ? "Adding..." : "ADD"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </motion.div>
    </div>
  );
}

/* ----------------- small helpers ----------------- */

function FormField({ id, label, type = "text" }) {
  return (
    <div className="mb-2">
      <label
        htmlFor={id}
        className="block text-xs sm:text-sm font-medium text-white"
      >
        {label}
      </label>
      <div className="border-0 border-b-2 border-white/40 focus-within:border-white mb-2">
        <input
          id={id}
          name={id}
          type={type}
          className="
            block w-full border-0 bg-transparent
            px-2 py-1 text-sm sm:text-base text-white
            placeholder:text-white/60
            focus:outline-none
          "
        />
      </div>
    </div>
  );
}

function slugify(v) {
  return String(v ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

/* helpers */
function unslugToName(slug) {
  return String(slug || "")
    .replace(/-/g, " ")
    .toUpperCase();
}
